import { Table } from 'antd';
import { CircleCheck, SearchX, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { withinSection } from '../../../../../shared/components/section-trail';
import { buildQueueColumns } from '../components/build-queue-columns';
import { DecideProposalDrawer } from '../components/DecideProposalDrawer';
import type { RateLineProposalQueueView } from '../../../rate-cards.api';
import { useApprovalQueue } from '../hooks/use-approval-queue';
import { useRateCardPermissions } from '../../../hooks/use-rate-card-permissions';

/** Every proposal waiting on a decision, across all cards, with what each would replace beside it. */
export function RateLineApprovalQueuePage() {
  const { state, list, isRefreshing, reload } = useApprovalQueue();
  const { userId } = useRateCardPermissions();
  const [deciding, setDeciding] = useState<RateLineProposalQueueView | null>(null);

  if (state.status === 'forbidden') return <Navigate to="/rate-cards" replace />;

  const columns = buildQueueColumns({ list, userId, onDecide: setDeciding });
  const empty = list.isFiltered ? (
    <EmptyState icon={SearchX} title="No matching proposals" description="No pending proposals match these filters." />
  ) : (
    <EmptyState icon={CircleCheck} title="Nothing waiting" description="Proposals submitted for approval appear here." />
  );

  return (
    <>
      <PageHeader breadcrumbs={withinSection('rate-cards')} title="Approval queue" description="Price changes waiting on a decision, across every rate card." />
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load the queue" description="Something went wrong loading the approval queue. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading approval queue" />}
      {state.status === 'loaded' && (
        <Table
          rowKey="id"
          dataSource={state.page.items}
          columns={columns}
          loading={isRefreshing}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: empty }}
          {...list.tableProps<RateLineProposalQueueView>(state.page.total)}
        />
      )}
      <DecideProposalDrawer proposal={deciding} onDecided={reload} onClose={() => setDeciding(null)} />
    </>
  );
}
